import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Mail, Send, BellRing, CalendarDays, Info, Download, Trash2, ShieldAlert } from "lucide-react";
import { toast } from "sonner";

export default function Settings() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [prefs, setPrefs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState("");
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    api.get("/profile/preferences").then((r) => setPrefs(r.data)).finally(() => setLoading(false));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const { notify_daily, notify_weekly, daily_time, timezone } = prefs;
      await api.put("/profile/preferences", { notify_daily, notify_weekly, daily_time, timezone });
      toast.success("Preferences saved");
    } catch {
      toast.error("Could not save");
    } finally {
      setSaving(false);
    }
  };

  const sendTest = async (kind) => {
    try {
      const { data } = await api.post(`/email/${kind === "digest" ? "test-digest" : "test-reminder"}`);
      if (data.sent) {
        toast.success(`Test ${kind} sent to ${user.email}`);
      } else {
        toast(`Email is currently disabled: ${data.reason}`, { duration: 5000 });
      }
    } catch {
      toast.error("Could not send test email");
    }
  };

  const exportData = async () => {
    try {
      const { data } = await api.get("/me/export");
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `alternavit-export-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Your data was downloaded");
    } catch {
      toast.error("Could not export your data");
    }
  };

  const deleteAccount = async () => {
    if (deleteConfirm !== "DELETE") {
      toast.error('Type "DELETE" exactly to confirm');
      return;
    }
    setDeleting(true);
    try {
      await api.delete("/me");
      toast.success("Your account and all data have been deleted");
      logout();
      nav("/");
    } catch {
      toast.error("Could not delete account");
      setDeleting(false);
    }
  };

  if (loading || !prefs) {
    return <main className="max-w-3xl mx-auto px-6 sm:px-8 py-16"><p style={{ color: "var(--text-2)" }}>Loading…</p></main>;
  }

  const serverEnabled = prefs.email_enabled_server;

  return (
    <main className="max-w-3xl mx-auto px-6 sm:px-8 py-12 sm:py-16" data-testid="settings-page">
      <header className="mb-10">
        <p className="label-eyebrow mb-2">Settings</p>
        <h1 className="font-serif text-4xl sm:text-5xl leading-tight">Reminders & emails</h1>
        <p className="mt-3" style={{ color: "var(--text-2)" }}>Quiet, intentional nudges &mdash; only when you want them.</p>
      </header>

      {!serverEnabled && (
        <div className="card-soft p-5 mb-8 flex items-start gap-3" style={{ background: "#FBF4F0", borderColor: "#ECDCD3" }} data-testid="email-disabled-banner">
          <Info className="w-4 h-4 mt-0.5" strokeWidth={1.5} style={{ color: "var(--warning)" }} />
          <div>
            <p className="text-sm" style={{ color: "var(--text)" }}>
              <strong>Email sending isn&rsquo;t configured yet.</strong> Your preferences will be saved, and emails will start
              flowing the moment the admin sets <code>RESEND_API_KEY</code> on the server (get one free at{" "}
              <a className="underline" href="https://resend.com" target="_blank" rel="noopener noreferrer">resend.com</a>).
            </p>
          </div>
        </div>
      )}

      <div className="space-y-4">
        <ToggleRow
          icon={BellRing}
          title="Daily supplement reminder"
          subtitle="A gentle nudge to log today's supplements."
          checked={prefs.notify_daily}
          onChange={(v) => setPrefs({ ...prefs, notify_daily: v })}
          testid="toggle-daily"
        />
        {prefs.notify_daily && (
          <div className="card-soft p-5 ml-0 sm:ml-12">
            <label className="label-eyebrow block mb-2">Send at</label>
            <input
              type="time"
              className="input-soft max-w-[180px]"
              value={prefs.daily_time}
              onChange={(e) => setPrefs({ ...prefs, daily_time: e.target.value })}
              data-testid="daily-time-input"
            />
            <p className="mt-2 text-xs" style={{ color: "var(--text-2)" }}>Local to your timezone ({prefs.timezone}).</p>
          </div>
        )}

        <ToggleRow
          icon={CalendarDays}
          title="Weekly progress digest"
          subtitle="A Sunday recap of your streak and consistency."
          checked={prefs.notify_weekly}
          onChange={(v) => setPrefs({ ...prefs, notify_weekly: v })}
          testid="toggle-weekly"
        />

        <div className="card-soft p-5">
          <label className="label-eyebrow block mb-2">Timezone (IANA)</label>
          <input
            type="text"
            className="input-soft"
            value={prefs.timezone}
            onChange={(e) => setPrefs({ ...prefs, timezone: e.target.value })}
            placeholder="UTC, America/New_York, Europe/Berlin…"
            data-testid="timezone-input"
          />
        </div>
      </div>

      <div className="mt-10 flex flex-wrap items-center gap-3">
        <button className="btn-primary" onClick={save} disabled={saving} data-testid="settings-save">
          {saving ? "Saving…" : "Save preferences"}
        </button>
        <button
          className="btn-ghost text-sm flex items-center gap-1.5"
          onClick={() => sendTest("reminder")}
          data-testid="send-test-reminder"
        >
          <Send className="w-4 h-4" strokeWidth={1.5} /> Send me a test reminder
        </button>
        <button
          className="btn-ghost text-sm flex items-center gap-1.5"
          onClick={() => sendTest("digest")}
          data-testid="send-test-digest"
        >
          <Mail className="w-4 h-4" strokeWidth={1.5} /> Send me a test digest
        </button>
      </div>

      <section className="mt-16 pt-10 border-t" style={{ borderColor: "var(--border)" }} data-testid="data-account-section">
        <p className="label-eyebrow mb-2">Data &amp; account</p>
        <h2 className="font-serif text-3xl leading-tight mb-3">Your rights under GDPR</h2>
        <p className="mb-6" style={{ color: "var(--text-2)" }}>
          You can download everything we hold about you, or delete your account permanently. Both are immediate.
        </p>

        <div className="grid sm:grid-cols-2 gap-3">
          <button
            onClick={exportData}
            className="card-soft p-5 text-left flex items-start gap-3 hover:bg-[var(--bg-2)] transition-colors"
            data-testid="export-data-button"
          >
            <Download className="w-5 h-5 mt-1 shrink-0" strokeWidth={1.5} style={{ color: "var(--brand)" }} />
            <div>
              <p className="font-serif text-xl mb-1">Download my data</p>
              <p className="text-sm" style={{ color: "var(--text-2)" }}>
                Get a JSON file with your profile, assessments, tracker logs and preferences.
              </p>
            </div>
          </button>

          <button
            onClick={() => setShowDeleteModal(true)}
            className="card-soft p-5 text-left flex items-start gap-3 hover:bg-[#FBF4F0] transition-colors"
            style={{ borderColor: "#ECDCD3" }}
            data-testid="delete-account-button"
          >
            <Trash2 className="w-5 h-5 mt-1 shrink-0" strokeWidth={1.5} style={{ color: "var(--error)" }} />
            <div>
              <p className="font-serif text-xl mb-1">Delete my account</p>
              <p className="text-sm" style={{ color: "var(--text-2)" }}>
                Permanently remove your account and every byte of data we have about you.
              </p>
            </div>
          </button>
        </div>
      </section>

      {showDeleteModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ background: "rgba(42,51,43,0.55)" }}
          data-testid="delete-modal"
          onClick={(e) => e.target === e.currentTarget && setShowDeleteModal(false)}
        >
          <div className="card-soft p-7 max-w-md w-full" style={{ background: "var(--bg)" }}>
            <ShieldAlert className="w-6 h-6 mb-3" strokeWidth={1.5} style={{ color: "var(--error)" }} />
            <h3 className="font-serif text-2xl mb-2">Delete your account?</h3>
            <p className="text-sm leading-relaxed mb-5" style={{ color: "var(--text-2)" }}>
              This permanently removes your profile, every assessment, every tracker entry, your preferences and any
              share links. It cannot be undone.
            </p>
            <label className="label-eyebrow block mb-2">Type DELETE to confirm</label>
            <input
              className="input-soft mb-4"
              value={deleteConfirm}
              onChange={(e) => setDeleteConfirm(e.target.value)}
              placeholder="DELETE"
              data-testid="delete-confirm-input"
              autoFocus
            />
            <div className="flex flex-wrap gap-2">
              <button
                onClick={deleteAccount}
                disabled={deleting || deleteConfirm !== "DELETE"}
                className="btn-primary text-sm"
                style={{ background: "var(--error)" }}
                data-testid="delete-confirm-button"
              >
                {deleting ? "Deleting…" : "Permanently delete"}
              </button>
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setDeleteConfirm("");
                }}
                className="btn-ghost text-sm"
                data-testid="delete-cancel"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
}

function ToggleRow({ icon: Icon, title, subtitle, checked, onChange, testid }) {
  return (
    <button
      onClick={() => onChange(!checked)}
      className={`selectable-card w-full text-left flex items-center justify-between ${checked ? "selected" : ""}`}
      data-testid={testid}
    >
      <div className="flex items-start gap-3">
        <Icon className="w-5 h-5 mt-0.5" strokeWidth={1.5} style={{ color: "var(--brand)" }} />
        <div>
          <p className="font-serif text-xl">{title}</p>
          <p className="text-sm" style={{ color: "var(--text-2)" }}>{subtitle}</p>
        </div>
      </div>
      <span
        className="w-12 h-7 rounded-full relative transition-colors"
        style={{ background: checked ? "var(--brand)" : "var(--border)" }}
      >
        <span
          className="absolute top-1 w-5 h-5 rounded-full bg-white transition-all"
          style={{ left: checked ? "calc(100% - 1.5rem)" : "0.25rem" }}
        />
      </span>
    </button>
  );
}
